import { CognitoErrorCodesEnum } from 'modules/auth/useAuth';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type HandlerResponse = 'success' | 'failed';

interface UseOtpStepProps {
  submitOtp: (otp: string, shouldVerifyAuthentication?: boolean) => Promise<HandlerResponse>;
  submitResendOtp: (email: string) => Promise<HandlerResponse>;
  submittedEmail: string | null;
  onOtpSuccess?: () => Promise<void>;
  onResendOtpSuccess?: () => void;
  shouldVerifyAuthentication?: boolean;
}

export const useOtpStep = ({
  submitOtp,
  submitResendOtp,
  submittedEmail,
  onOtpSuccess,
  shouldVerifyAuthentication = true,
}: UseOtpStepProps) => {
  const { t } = useTranslation();

  const handleOtpSubmit = useCallback(
    async (otp: string): Promise<HandlerResponse> => {
      try {
        const res = await submitOtp(otp, shouldVerifyAuthentication);
        if (res !== 'success') {
          return 'failed';
        }
        if (onOtpSuccess) {
          await onOtpSuccess();
        }
        return 'success';
      } catch (err) {
        if (typeof err === 'object' && err !== null && 'message' in err) {
          const errorCode = (err as any).message;

          if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
            if (err.message === CognitoErrorCodesEnum.NotAuthorizedException) {
              throw new Error(t('auth:incorrectOtp'));
            }
            throw new Error(t(`auth:cognitoErrors.${errorCode}`));
          }
        }
        throw new Error(t('auth:validationMessages.unknownError'));
      }
    },
    [submitOtp, t, onOtpSuccess],
  );

  const handleResendOtp = useCallback(async (): Promise<HandlerResponse> => {
    if (!submittedEmail) {
      return 'failed';
    }
    try {
      await submitResendOtp(submittedEmail);
      return 'success';
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'message' in err) {
        const errorCode = (err as any).message;

        if (Object.values(CognitoErrorCodesEnum).includes(errorCode)) {
          throw new Error(t(`auth:cognitoErrors.${errorCode}`));
        }
      }
      throw new Error(t('auth:validationMessages.unknownError'));
    }
  }, [submittedEmail, submitResendOtp, t]);

  return { handleOtpSubmit, handleResendOtp };
};
